import { getInherittedSettingValueV2, getScreenSettings, mergeCompositeSetting } from "@client/lib/settings";
import { getStyleSheetColor } from "@client/lib/styleSheet";
import { type CompositeSettings, type LocalSettings, type MainSettings, ScreenType } from "@client/types";
import { createSelector } from "reselect";
import { getSettingValueSelector } from ".";
import * as stateSelectors from "../state";
import { styleSheetDefaultSettingsSelector } from "../state";
import { type OutputParametricSelector } from "../types";

const mergeCompositeSettingInner = <T extends {}>(
  settings: MainSettings,
  localSettings: LocalSettings,
  screen: ScreenType
): T | null =>
  mergeCompositeSetting<T>(
    (screen) => getScreenSettings(settings, screen),
    localSettings ? (screen) => getScreenSettings(localSettings, screen) : null,
    screen
  );

export type StyleSheetLink = {
  color: string;
  decoration: "underline" | undefined;
  hoverColor: string;
  hoverDecoration: "underline" | undefined;
};
const styleSheetSelectors = {
  colors: {
    primary: createSelector(
      [
        styleSheetDefaultSettingsSelector.primaryColor,
        styleSheetDefaultSettingsSelector.local.primaryColor,
        stateSelectors.screenSelector
      ],
      (settings, localSettings, screen) =>
        getInherittedSettingValueV2(settings, localSettings, undefined, "color", screen)
    ),
    text: createSelector(
      [
        styleSheetDefaultSettingsSelector.textColor,
        styleSheetDefaultSettingsSelector.local.textColor,
        stateSelectors.screenSelector
      ],
      (settings, localSettings, screen) =>
        getInherittedSettingValueV2(settings, localSettings, undefined, "color", screen)
    )
  },
  link: createSelector(
    [
      styleSheetDefaultSettingsSelector.link,
      styleSheetDefaultSettingsSelector.local.link,
      stateSelectors.screenSelector
    ],
    mergeCompositeSettingInner<CompositeSettings.Link>
  ),
  typography: {
    h1: createSelector(
      [styleSheetDefaultSettingsSelector.h1, styleSheetDefaultSettingsSelector.local.h1, stateSelectors.screenSelector],
      mergeCompositeSettingInner<CompositeSettings.Text>
    ),
    h2: createSelector(
      [styleSheetDefaultSettingsSelector.h2, styleSheetDefaultSettingsSelector.local.h2, stateSelectors.screenSelector],
      mergeCompositeSettingInner<CompositeSettings.Text>
    ),
    h3: createSelector(
      [styleSheetDefaultSettingsSelector.h3, styleSheetDefaultSettingsSelector.local.h3, stateSelectors.screenSelector],
      mergeCompositeSettingInner<CompositeSettings.Text>
    ),
    h4: createSelector(
      [styleSheetDefaultSettingsSelector.h4, styleSheetDefaultSettingsSelector.local.h4, stateSelectors.screenSelector],
      mergeCompositeSettingInner<CompositeSettings.Text>
    ),
    h5: createSelector(
      [styleSheetDefaultSettingsSelector.h5, styleSheetDefaultSettingsSelector.local.h5, stateSelectors.screenSelector],
      mergeCompositeSettingInner<CompositeSettings.Text>
    ),
    h6: createSelector(
      [styleSheetDefaultSettingsSelector.h6, styleSheetDefaultSettingsSelector.local.h6, stateSelectors.screenSelector],
      mergeCompositeSettingInner<CompositeSettings.Text>
    ),
    text: createSelector(
      [
        styleSheetDefaultSettingsSelector.text,
        styleSheetDefaultSettingsSelector.local.text,
        stateSelectors.screenSelector
      ],
      mergeCompositeSettingInner<CompositeSettings.Text>
    )
  }
};
export default styleSheetSelectors;

const getColorSelectorBase = (
  target: string,
  name: string,
  defaultValueSelector: OutputParametricSelector<any, any, "primary" | "text" | string, any>,
  prefix?: string
) => {
  const colorSelector = getSettingValueSelector(target, name, prefix);

  return createSelector(
    [
      stateSelectors.styleSheetDataSettingsSelector,
      stateSelectors.styleSheetDataLocalSelector,
      colorSelector,
      stateSelectors.screenSelector,
      defaultValueSelector
    ],
    (styleSheetSettings, localStyleSheetSettings, value, screen, defaultValue) => {
      return getStyleSheetColor(styleSheetSettings, localStyleSheetSettings, value, screen, defaultValue);
    }
  );
};

export const getColorSelector = (
  target: string,
  name: string,
  prefix?: string,
  fallback?: "text" | "primary" | "none" | string
) => {
  const fallbackSelector = createSelector([], () => fallback ?? "primary");

  return getColorSelectorBase(target, name, fallbackSelector, prefix);
};

export const getColorWithDefaultSelector = getColorSelectorBase;
